import { keyLabel, type CommandRegistry, type KeyInfo } from "./commands.ts";
import type { ViewTab } from "./editors.ts";

/**
 * The keyboard shortcuts tab: every key in the registry, grouped by category
 * in the order the categories were registered, with a filter on the command's
 * name and the key's label. It is drawn again when the registry changes.
 */
export function shortcutsTab(registry: CommandRegistry): ViewTab {
    return {
        id: "keyboard-shortcuts",
        title: "Keyboard Shortcuts",
        icon: "keyboard",
        create: () => {
            const element = document.createElement("div");
            element.className = "wb-shortcuts";
            const filter = document.createElement("input");
            filter.type = "text";
            filter.placeholder = "Type to search in keybindings";
            filter.setAttribute("aria-label", filter.placeholder);
            filter.autofocus = true;
            const table = document.createElement("table");
            element.append(filter, table);
            const draw = () => table.replaceChildren(...rows(registry.bindings(), filter.value));
            filter.addEventListener("input", draw);
            draw();
            return { element, dispose: registry.onChange(draw) };
        },
    };
}

function rows(bindings: KeyInfo[], query: string): HTMLElement[] {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const groups = new Map<string, KeyInfo[]>();
    for (const info of bindings) {
        const labels = info.keys.map(keyLabel);
        const text = `${info.category ?? ""} ${info.title} ${labels.join(" ")}`.toLowerCase();
        if (!words.every((word) => text.includes(word))) continue;
        const category = info.category ?? "General";
        groups.set(category, [...(groups.get(category) ?? []), info]);
    }
    const out: HTMLElement[] = [];
    for (const [category, infos] of groups) {
        const head = document.createElement("tr");
        const th = document.createElement("th");
        th.colSpan = 2;
        th.scope = "colgroup";
        th.textContent = category;
        head.append(th);
        out.push(head);
        for (const info of infos) {
            const row = document.createElement("tr");
            const title = document.createElement("td");
            title.textContent = info.title;
            const keys = document.createElement("td");
            for (const key of info.keys) {
                const kbd = document.createElement("kbd");
                kbd.textContent = keyLabel(key);
                keys.append(kbd);
            }
            row.append(title, keys);
            out.push(row);
        }
    }
    if (out.length === 0) {
        const row = document.createElement("tr");
        const cell = document.createElement("td");
        cell.colSpan = 2;
        cell.className = "wb-shortcuts-none";
        cell.textContent = "No keys match";
        row.append(cell);
        out.push(row);
    }
    return out;
}
